import {
  CategoryScale,
  Chart as ChartJS,
  Filler,
  LineController,
  LineElement,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js'
import zoomPlugin from 'chartjs-plugin-zoom'
import { memo, useEffect, useMemo, useRef } from 'react'

ChartJS.register(
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Title,
  Tooltip,
  Filler,
  zoomPlugin
)

type Props = {
  duration: number
  signals: number[]
  offset: 0 | 20 | 40
}

const ECG_LIVE_FREQUENCY = 300

function EcgArrhythmiaChart({ signals, offset, duration }: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const chartRef = useRef<ChartJS | null>(null)

  const data = useMemo(() => {
    const startIndex = offset ? ECG_LIVE_FREQUENCY * offset - 1 : 0
    const endIndex = ECG_LIVE_FREQUENCY * (offset + duration) - 1
    return signals.slice(startIndex, endIndex)
  }, [offset, signals, duration])

  const labels = useMemo(() => {
    return Array.from({ length: data.length }, (_, index) =>
      (index / ECG_LIVE_FREQUENCY + offset).toFixed(4)
    )
  }, [data, offset])

  useEffect(() => {
    if (canvasRef.current && !chartRef.current) {
      const ctx = canvasRef.current.getContext('2d')

      if (!ctx) return

      chartRef.current = new ChartJS(ctx, {
        data: {
          datasets: [
            {
              borderColor: 'rgb(0,0,0)',
              borderWidth: 1,
              data,
              label: 'ECG',
              pointRadius: 0,
              tension: 0.1,
            },
          ],
          labels,
        },
        options: {
          elements: {
            line: {
              tension: 0,
            },
          },
          interaction: {
            axis: 'x',
            intersect: false,
            mode: 'nearest',
          },
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: false,
            },
            tooltip: {
              enabled: true,
              intersect: false,
              mode: 'index',
            },
            zoom: {
              limits: {
                x: {
                  max: 'original',
                  min: 'original',
                },
                y: {
                  max: 2,
                  min: -2,
                },
              },
              pan: {
                enabled: true,
                mode: 'x',
              },
              zoom: {
                mode: 'x',
                pinch: {
                  enabled: true,
                },
                wheel: {
                  enabled: true,
                },
              },
            },
          },
          responsive: true,
          scales: {
            x: {
              grid: {
                color: (context) => {
                  return isMajorGridLine(context.tick.value, 0.2, 0.05)
                    ? '#dc2626'
                    : '#F87171'
                },
                lineWidth: (context) => {
                  return isMajorGridLine(context.tick.value, 0.2, 0.05)
                    ? 0.5
                    : 0.15
                },
              },
              ticks: {
                autoSkip: false,
                callback: function (value, _index, _ticks) {
                  const tickValue = value as number
                  return isMajorGridLine(tickValue, 1)
                    ? `${tickValue.toFixed(0)}s`
                    : ''
                },
                font: {
                  size: 10,
                },
                maxRotation: 0,
                stepSize: 0.04,
              },
              type: 'linear',
              title: {
                display: true,
                text: 'Time (s)',
              },
            },
            y: {
              title: {
                display: true,
                text: 'Voltage (mV)',
              },
              grid: {
                color: (context) => {
                  return isMajorGridLine(context.tick.value, 0.5, 0.05)
                    ? '#dc2626'
                    : '#F87171'
                },
                lineWidth: (context) => {
                  return isMajorGridLine(context.tick.value, 0.5, 0.05)
                    ? 0.5
                    : 0.15
                },
              },
              max: 1.5,
              min: -1.5,
              ticks: {
                autoSkip: false,
                callback: function (value, _index, _ticks) {
                  const tickValue = value as number
                  return isMajorGridLine(tickValue, 0.5)
                    ? tickValue.toFixed(2)
                    : ''
                },
                font: {
                  size: 10,
                },
                stepSize: 0.1,
              },
            },
          },
        },
        type: 'line',
      })
    }

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy()
        chartRef.current = null
      }
    }
  }, [canvasRef, data, labels])

  return <canvas ref={canvasRef}></canvas>
}

const MemoizedEcgArrhythmiaChart = memo(EcgArrhythmiaChart)

export { EcgArrhythmiaChart, MemoizedEcgArrhythmiaChart }

function isMajorGridLine(
  tickValue: number,
  divisor: number,
  threshold: number = 0
): boolean {
  return Math.abs(Math.abs(tickValue * 10) % (divisor * 10)) <= threshold
}
